// @ts-nocheck
import axiosOriginal from 'axios';
import type { AxiosRequestConfig, AxiosError } from 'axios';

import qs from 'qs';
import config from './config';
import { getAccessToken, getRefreshToken, setTokens, clearTokens } from './token-storage';

/** --- AXIOS INSTANCE --- */

const formatParams = (params: Record<string, unknown>): string =>
  qs.stringify(params, { indices: false, arrayFormat: 'repeat' });

export const axios = axiosOriginal.create({
  baseURL: config.apiUrl,
  paramsSerializer: formatParams,
});

/** ------- */

/** --- REQUEST INTERCEPTOR --- */

interface RequestConfig extends AxiosRequestConfig {
  headers?: Record<string, string>;
}

/** ------- */

axios.interceptors.request.use(
  async (requestConfig: RequestConfig): Promise<RequestConfig> => {
    const token = getAccessToken();
    if (token) {
      return {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          Authorization: `Bearer ${token}`,
        },
      };
    }

    return requestConfig;
  },
  error => Promise.reject(error)
);

/** ------- */

/** --- RESPONSE INTERCEPTOR: single-flight refresh --- */

const REFRESH_URL = `${config.gatewayApiUrl}/public/v1/auth/refresh`;

// Shared across every concurrent request: if 5 calls 401 at once we run ONE
// /refresh and the other 4 await the same promise. Parallel /refresh calls with
// the same refresh token look like reuse to the backend and revoke the session.
let refreshPromise: Promise<string | null> | null = null;

// Latched the moment we decide to end the session this page-life. Two jobs:
//   1) collapse a burst of concurrent 401s into a SINGLE reload;
//   2) stop a /refresh that was already in flight from re-saving tokens AFTER
//      logout — otherwise the rotated tokens survive the reload, the app
//      re-fires its authed bootstrap requests, they 401 again, and we loop
//      reloading forever (the "login page keeps reloading" bug).
// Module-level, so it resets to false on the next fresh page load.
let sessionTerminated = false;

const doRefresh = async (): Promise<string | null> => {
  if (sessionTerminated) return null;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;
  try {
    // RAW axios — not the intercepted instance — so the dead access token is
    // never attached and a 401 here can't recurse back into this interceptor.
    const { data } = await axiosOriginal.post(
      REFRESH_URL,
      { refresh_token: refreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    );
    // If we logged out while this request was in flight, don't resurrect the
    // session — the pending reload must land on a token-less page.
    if (sessionTerminated) return null;
    // Rotate: replace BOTH tokens, discard the old refresh token.
    setTokens(data.access_token, data.refresh_token);
    return data.access_token;
  } catch (error) {
    // 401 from /refresh is terminal (expired / unknown / reused). A network blip
    // is not — rethrow so we don't nuke a still-valid session on a transient error.
    if ((error as AxiosError)?.response?.status === 401) return null;
    throw error;
  }
};

const refreshOnce = (): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// For calls made outside this instance, e.g. Laila's streamed chat, which
// reads its answer with fetch: the same single-flight refresh.
export const refreshAccessToken = refreshOnce;

const goToLogin = () => {
  // Concurrent 401s all land here — only the first one terminates and reloads.
  if (sessionTerminated) return;
  sessionTerminated = true;
  clearTokens();
  // Route guards (AuthorizedRoute) redirect to login once there's no user.
  // Reloading forces that immediately and works for both HashRouter and
  // BrowserRouter apps without hard-coding a path.
  if (global.location) {
    global.location.reload();
  }
};

// Auth endpoints must never trigger a refresh-retry: a 401 from login (bad
// creds) / refresh / logout is meaningful on its own and is handled by the
// caller (e.g. "wrong password" toast).
const isAuthEndpoint = (url?: string) => !!url && url.includes('/public/v1/auth/');

// axios v1 wraps request headers in AxiosHeaders (case-insensitive .get());
// plain objects can still show up from custom configs — support both.
const getAuthHeader = (request: RequestConfig): string | undefined => {
  const headers = request.headers as any;
  if (!headers) return undefined;
  return typeof headers.get === 'function' ? headers.get('Authorization') : headers.Authorization;
};

axios.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const status = error?.response?.status;
    const originalRequest = error?.config as (RequestConfig & { _retry?: boolean }) | undefined;

    // 403 is a permissions error — never refresh. generateErrorObject turns it
    // into a "No access" notification, so every handleServerError call site
    // reports it without special-casing.
    // Auth endpoints (login/refresh/logout) handle their own 401s.
    if (status !== 401 || !originalRequest || isAuthEndpoint(originalRequest.url)) {
      return Promise.reject(error);
    }

    // A 401 on a request that never carried a token is not an expired session —
    // it's an unauthenticated call (e.g. fired from the login screen). Refreshing
    // or reloading here would reload the login page in an endless loop.
    if (!getAuthHeader(originalRequest)) {
      return Promise.reject(error);
    }

    // A second 401 after we already retried is terminal — clear tokens and go
    // to login. One retry per request, no loops.
    if (originalRequest._retry) {
      goToLogin();
      return Promise.reject(error);
    }

    const newAccessToken = await refreshOnce();
    if (!newAccessToken) {
      goToLogin();
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    originalRequest.headers = {
      ...originalRequest.headers,
      Authorization: `Bearer ${newAccessToken}`,
    };
    return axios(originalRequest);
  }
);
